import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Zap, Flag, ArrowRight } from "lucide-react";
import ProgressRingLight from "./ProgressRingLight";
import {
    projectCompletionField,
    projectNextMilestone,
    projectStageLabel,
} from "@/lib/projectPresentation";

/**
 * ProjectDesktopCard — desktop-optimised project card. Mirrors mobile
 * ProjectDesktopCard.tsx: customer, address, system pill, current-stage
 * pill, next milestone, and a large progress ring.
 *
 * The ring itself is now interactive — it navigates directly to the
 * project's Phases & Tasks section (Journey/Milestones equivalent),
 * bypassing the overview. Clicking anywhere else on the card still
 * routes to the project detail page.
 */
function toneForProgress(p) {
    if (p >= 100) return { bg: "#E6F1DE", fg: "#2E7E22" };
    if (p > 0) return { bg: "#FEEDE5", fg: "#B45309" };
    return { bg: "#E8E8ED", fg: "#4B5565" };
}

export default function ProjectDesktopCard({ project, testid }) {
    const navigate = useNavigate();
    const tone = toneForProgress(project.progress || 0);
    const projectLabel = project.reference
        ? `Project ${project.reference}`
        : project.customer_name;
    // FIELD-001C Section C · Every visible caption on the card
    // flows through the shared presentation helper so it agrees
    // with the detail page for the same project. Empty-phase or
    // stubby backend data must never surface as "Complete" here.
    const stageText = projectStageLabel(project);
    const milestoneText = projectNextMilestone(project);
    const completion = projectCompletionField(project);
    const showMilestone = milestoneText && milestoneText !== "Not recorded";

    return (
        <Link
            to={`/installer/project/${project.id}`}
            data-testid={testid}
            className="pe-card pe-card-hover p-5 flex flex-col gap-3 min-h-[240px] transition-shadow"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="text-lg text-[#131B2F] font-medium tracking-tight truncate">{project.customer_name}</div>
                    <div className="flex items-center gap-1 mt-1">
                        <MapPin size={13} strokeWidth={1.75} className="text-[#4B5565] shrink-0" />
                        <span className="text-sm text-[#4B5565] truncate">{project.address}</span>
                    </div>
                </div>
                <ProgressRingLight
                    progress={project.progress || 0}
                    size={64}
                    strokeWidth={7}
                    testid={`proj-ring-${project.id}`}
                    onActivate={() => navigate(`/installer/project/${project.id}#phases`)}
                    ariaLabel={`View installation journey for ${projectLabel} — ${project.progress || 0}% complete`}
                />
            </div>

            <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1.5 rounded-full max-w-full" style={{ background: "#F1F8EC" }}>
                <Zap size={13} strokeWidth={1.75} className="text-[#2E7E22] shrink-0" />
                <span className="text-sm text-[#2E7E22] font-medium truncate">{project.system_summary}</span>
            </span>

            <div>
                <div className="flex items-center justify-between gap-3">
                    <div className="text-[11px] text-[#4B5565] font-medium tracking-[0.08em] uppercase">Current stage</div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full max-w-[70%]" style={{ background: tone.bg }}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tone.fg }} />
                        <span className="text-sm font-medium truncate" data-testid={`pdc-stage-${project.id}`} style={{ color: tone.fg }}>{stageText}</span>
                    </span>
                </div>
                {showMilestone && (
                    <div className="flex items-start gap-1.5 mt-2">
                        <Flag size={13} strokeWidth={1.75} className="text-[#4B5565] shrink-0 mt-0.5" />
                        <div className="text-sm text-[#4B5565] leading-relaxed">
                            Next: <span className="text-[#131B2F] font-medium" data-testid={`pdc-milestone-${project.id}`}>{milestoneText}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-auto pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
                <div className="text-sm text-[#4B5565]" data-testid={`pdc-completion-${project.id}`} data-tone={completion.tone}>
                    <span data-testid={`pdc-completion-label-${project.id}`}>{completion.label}</span>
                    {" · "}
                    <span data-testid={`pdc-completion-value-${project.id}`}>{completion.value}</span>
                </div>
                <div className="inline-flex items-center gap-1 text-sm text-[#3E9E2E] font-medium">
                    Open <ArrowRight size={14} strokeWidth={1.75} />
                </div>
            </div>
        </Link>
    );
}
